import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { REMINDER_DEFAULT_OFFSET, REMINDER_DEFAULT_OFFSET_KEY, offsetMinutesOf, type ReminderDefaultOffset } from '@/src/renderer/lib/reminders/preference'
import { reminderRowsOf, type TaskReminderRows } from '@/src/renderer/lib/reminders/reminderRows'

export type { ReminderRow, TaskReminderRows } from '@/src/renderer/lib/reminders/reminderRows'

const MINUTE = 60_000

/** „Heute, 13:30“ wird irgendwann „Vorbei“ — die Uhr rückt minütlich. */
function useMinuteNow(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), MINUTE)
    return () => clearInterval(timer)
  }, [])
  return now
}

/**
 * **Die Erinnerungen einer Aufgabe, fertig beschriftet** (Plan task-reminders, Etappe 5) — für den
 * Chip im Kopf der Aufgaben-Details und seine Liste. `undefined`, bis alles geladen ist.
 */
export function useTaskReminderRows(taskId: string | undefined): TaskReminderRows | undefined {
  const { t, i18n } = useTranslation()
  const source = useDataSource()
  const reminders = source.useTaskReminders(taskId)
  const ids = useMemo(() => (taskId ? [taskId] : []), [taskId])
  const chains = source.useTaskChains(ids)
  const [offset, , ready] = source.usePreference<ReminderDefaultOffset>(REMINDER_DEFAULT_OFFSET_KEY, REMINDER_DEFAULT_OFFSET)
  const now = useMinuteNow()
  const locale = i18n.language || 'de'

  return useMemo(() => {
    const chain = taskId ? chains?.get(taskId) : undefined
    if (!reminders || !chain?.[0] || !ready) return undefined
    return reminderRowsOf(chain, reminders, offsetMinutesOf(offset), now, t, locale)
  }, [taskId, chains, reminders, ready, offset, now, t, locale])
}
